import { createContext, useState, useEffect, useCallback } from "react";
import { getToken, saveToken, removeToken, decodeToken } from "../utils/auth";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

export const AuthContext = createContext();

let logoutTimer; // global timer reference

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(getToken());
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  });

  const navigate = useNavigate();

  /** ✅ Logout */
  const logout = useCallback(() => {
    removeToken();
    localStorage.removeItem("user");

    setToken(null);
    setUser(null);

    if (logoutTimer) clearTimeout(logoutTimer);

    navigate("/login");
  }, [navigate]);

  /** ✅ Auto Logout Scheduler */
  const scheduleAutoLogout = useCallback(
    (duration) => {
      if (logoutTimer) clearTimeout(logoutTimer);

      if (!duration || duration <= 0) {
        logout();
        return;
      }

      logoutTimer = setTimeout(() => {
        toast.info("Your session has expired. Please log in again.");
        logout();
      }, duration);
    },
    [logout]
  );

  /** ✅ Login */
  const login = useCallback(
    (newToken) => {
      if (!newToken || typeof newToken !== "string") {
        console.log("❌ Invalid token passed to login()");
        return;
      }

      const decodedUser = decodeToken(newToken);

      if (!decodedUser) {
        logout();
        return;
      }

      saveToken(newToken);
      setToken(newToken);
      setUser(decodedUser);

      localStorage.setItem("user", JSON.stringify(decodedUser));

      const remainingTime = decodedUser.exp * 1000 - Date.now();

      console.log("✅ Login Remaining Time:", remainingTime);

      if (remainingTime <= 0) {
        logout();
        return;
      }

      scheduleAutoLogout(remainingTime);
    },
    [logout, scheduleAutoLogout]
  );

  /** ✅ Restore Session on Reload */
  useEffect(() => {
    const storedToken = getToken();

    if (!storedToken) return;

    const decodedUser = decodeToken(storedToken);

    if (!decodedUser) {
      localStorage.removeItem("user");
      logout();
      return;
    }

    setToken(storedToken);
    setUser(decodedUser);

    const remainingTime = decodedUser.exp * 1000 - Date.now();

    console.log("🔁 Restore Remaining Time:", remainingTime);

    if (remainingTime <= 0) {
      logout();
      return;
    }

    scheduleAutoLogout(remainingTime);
  }, [logout, scheduleAutoLogout]);

  /** ✅ Manual User Update */
  // const updateUser = useCallback((updatedUser) => {
  //   setUser(updatedUser);
  //   localStorage.setItem("user", JSON.stringify(updatedUser));
  // }, []);
// In your AuthContext.js or similar
const updateUser = (userData, newToken = null) => {
  setUser(prevUser => ({
    ...prevUser,
    ...userData
  }));
  
  if (newToken) {
    setToken(newToken);
    localStorage.setItem('token', newToken);
  }
};
  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        login,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};